import React, {useState} from "react";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {GridTable} from "../../../../Components/Grid";
import {OwnLoanRow} from "./OwnLoanRow";
import {copyToClipboard} from "./ownLoanUtility";
import {OwnLoanEditFormView} from "./OwnLoanEditFormView";
import {DialogBox} from "../../../../Components/Dialog/DialogBox";
import {isNullOrUndefined} from "../../../../Utilities/commonUtilities";
import {OwnLoanItem, OwnLoanSearchCriteria} from "./ownLoan.types";

interface OwnLoanTableProps {
    ownLoanItems: OwnLoanItem[];
    ownLoanSearchCriteria: OwnLoanSearchCriteria;
    canManageOwnLoans: boolean;
    handleSetOwnLoanSearchCriteria: (criteria: OwnLoanSearchCriteria) => void;
    handleSaveUpdatedOwnLoanItem: (item: OwnLoanItem) => void;
    updateOwnLoanItemInState: (itemId: string, status: string | number, notes: string | null) => void;
}

export const OwnLoanTable = ({
                                 ownLoanItems,
                                 ownLoanSearchCriteria,
                                 canManageOwnLoans,
                                 handleSetOwnLoanSearchCriteria,
                                 handleSaveUpdatedOwnLoanItem,
                                 updateOwnLoanItemInState,
                             }: OwnLoanTableProps) => {

    const [ownLoanItemForDialog, setOwnLoanItemForDialog] = useState<OwnLoanItem | null>(null);
    const [isOwnLoanEditDialogOpen, setIsOwnLoanEditDialogOpen] = useState(false);

    ownLoanSearchCriteria?.ApplySorting && ownLoanSearchCriteria.ApplySorting(ownLoanSearchCriteria, ownLoanItems);

    const handleOrderClick = (event: React.MouseEvent<HTMLElement>) => {
        let criteria = {...ownLoanSearchCriteria};
        const columnId = (event.target as HTMLElement).dataset.id;
        if (columnId) criteria.UpdateSorting(columnId);
        criteria.loadSearchResults = false;
        handleSetOwnLoanSearchCriteria(criteria);
    };


    const handleOpenEditDialog = (event: React.MouseEvent<HTMLElement>, ownLoanItem: OwnLoanItem) => {
        event.preventDefault();

        setOwnLoanItemForDialog(ownLoanItem);
        setIsOwnLoanEditDialogOpen(true);
    }

    const handleCloseEditDialog = () => setIsOwnLoanEditDialogOpen(false);


    const handleSaveOwnLoanItem = () => {
        if (ownLoanItemForDialog) {
            handleSaveUpdatedOwnLoanItem(ownLoanItemForDialog);
            handleCloseEditDialog();
        }
    };

    const handleUpdateOwnLoanStatus = (ownLoanItemId: string, ownLoanStatus: string | number) => {
        updateOwnLoanItemInState(ownLoanItemId, ownLoanStatus, null);
    }


    const isLoanOwnItemsEmpty = isArrayNullOrEmpty(ownLoanItems);
    const canOpenOwnLoanItemEditDialog = canManageOwnLoans && isOwnLoanEditDialogOpen && ownLoanItemForDialog;

    return <>
        <GridTable className="searchResults" parentClass={`margin--topNone`}>
            <thead>
            <tr>
                <th onClick={handleOrderClick} className="is-clickable" data-id="dateCreatedUtc">Order Submitted
                    Date
                </th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="ownCatalogItemTitle">Title</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="quantity">Quantity</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="ownCatalogItemCost">Running Cost</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="patronFullName">Patron</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="districtName">District</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="organization">Organization</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="shippingLocationName">Shipping Location
                </th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="ownCatalogNumber">Catalog Number</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="ownLoanStatusText">Status</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="ownLoanNotes">Notes</th>
                <th scope={"col"} className={"align-center"}>Actions</th>
            </tr>
            </thead>
            <tbody>
            {
                !isLoanOwnItemsEmpty &&
                ownLoanItems.map((ownLoanItem) => {

                    const handleCopyToClipboard = async () => {
                        const shippingLocationDetails = ownLoanItem.shippingLocation;
                        if (isNullOrUndefined(shippingLocationDetails)) return;

                        const addressToCopy = `${shippingLocationDetails.name}\n${shippingLocationDetails.address}\n${!isNullOrUndefined(shippingLocationDetails.address2) ? shippingLocationDetails.address2.trim() + '\n' : ''}${shippingLocationDetails.city}, ${shippingLocationDetails.state} ${shippingLocationDetails.zipCode}`;

                        await copyToClipboard(addressToCopy);
                    };

                    return <OwnLoanRow
                        key={ownLoanItem.ownLoanItemId}
                        ownLoanItem={ownLoanItem}
                        canManageOwnLoans={canManageOwnLoans}
                        handleCopyToClipboard={handleCopyToClipboard}
                        handleOpenEditDialog={handleOpenEditDialog}
                        handleUpdateOwnLoanStatus={handleUpdateOwnLoanStatus}
                    />;
                })
            }
            {
                isLoanOwnItemsEmpty &&
                <tr data-testid={`no-order-row`}>
                    <td colSpan={12} className={`text-center`}>No results found.</td>
                </tr>
            }
            </tbody>
        </GridTable>
        {
            canOpenOwnLoanItemEditDialog &&
            <DialogBox
                handleCancel={handleCloseEditDialog}
                handleSave={handleSaveOwnLoanItem}
                openDialog={isOwnLoanEditDialogOpen}
                title={`Edit Own Item: ${ownLoanItemForDialog.ownCatalogItemTitle}`}>
                <OwnLoanEditFormView
                    ownLoanItemForDialog={ownLoanItemForDialog}
                    setOwnLoanItemForDialog={setOwnLoanItemForDialog}/>
            </DialogBox>
        }
    </>;
};
